import { css } from '@emotion/react';
import { type HTMLProps, type MemoExoticComponent, memo } from 'react';

import type { AvatarShape } from '@didaskill/types/web';
import { useFocus } from '@didaskill/ui/utils';

import { Avatar } from '../avatar/Avatar';
import type { AVATAR_SIZES } from '../avatar/constants';

export type AvatarButtonProps = Omit<HTMLProps<HTMLButtonElement>, 'type' | 'size' | 'ref'> & {
  /** User to whom the avatar belongs. */
  user: AvatarShape;
  /** Avatar size. */
  size?: AVATAR_SIZES;
  /** Display the avatar with a shadow. */
  isShadowed?: boolean;
};

export const AvatarButtonBase = ({
  user,
  size = Avatar.SIZES.MEDIUM,
  isShadowed = false,
  ...props
}: AvatarButtonProps) => {
  const { isFocused, ...focusProps } = useFocus(props);

  return (
    <button
      {...props}
      {...focusProps}
      type="button"
      css={css`
        background: none;
        color: inherit;
        border: none;
        padding: 0;
        font: inherit;
        cursor: pointer;
        outline: inherit;
        display: block;
      `}
    >
      <Avatar user={user} size={size} isShadowed={isFocused || isShadowed} />
    </button>
  );
};

AvatarButtonBase.displayName = 'AvatarButton';

export const AvatarButton: MemoExoticComponent<typeof AvatarButtonBase> & {
  SIZES?: typeof AVATAR_SIZES;
} = memo(AvatarButtonBase);

AvatarButton.SIZES = Avatar.SIZES;
